import { Box, chakra, useMultiStyleConfig } from '@chakra-ui/react';
import type { SystemStyleObject } from '@chakra-ui/styled-system';
import { t } from '@transifex/native';
import { Link } from 'react-router-dom';
import {
	useCallback,
	useEffect,
	useState,
	memo,
	type KeyboardEvent,
} from 'react';
import { goTo } from '../../../@xmcloud/core/routes/paths';
import { handleTo } from '../../../@xmcloud/utils/helpers';
import { TabItems } from './items';

const ChakraRouterLink = chakra(Link);

interface Props {
	tabIndex: number;
	tabItems: TabItems[];
	state: { projectName?: string; environmentName?: string; host?: string };
	args: string[];
}

export const XMCSTabs: React.FC<Props> = memo(
	({ tabIndex, tabItems, args }) => {
		const formatAndCleanTestId = useCallback((fnName: string) => {
			const cleanName = fnName.startsWith('old')
				? fnName.slice(3).charAt(0).toLowerCase() + fnName.slice(4)
				: fnName;

			return cleanName;
		}, []);

		const styles = useMultiStyleConfig('Tabs', {
			variant: 'line',
			colorScheme: 'primary',
			size: 'md',
			orientation: 'horizontal',
		});

		const tabStyles = styles.tab as SystemStyleObject & {
			_selected?: SystemStyleObject;
		};
		const { _selected: _tabSelected, ...tabBaseStyles } = tabStyles;
		const selectedTabStyles = _tabSelected ?? {};

		/** Roving tabindex: one tab stop in the nav strip; arrows move focus between links. */
		const [rovingFocusIndex, setRovingFocusIndex] = useState(tabIndex);

		useEffect(() => {
			setRovingFocusIndex(tabIndex);
		}, [tabIndex]);

		const onListKeyDown = useCallback(
			(event: KeyboardEvent<HTMLElement>) => {
				const listEl = event.currentTarget;
				const links = Array.from(
					listEl.querySelectorAll<HTMLAnchorElement>('a[href]'),
				);
				if (!links.length) return;

				let index = links.indexOf(
					document.activeElement as HTMLAnchorElement,
				);

				const focusAt = (i: number) => {
					links[i]?.focus();
				};

				switch (event.key) {
					case 'ArrowRight':
						event.preventDefault();
						if (index === -1) index = 0;
						else index = (index + 1) % links.length;
						focusAt(index);
						break;
					case 'ArrowLeft':
						event.preventDefault();
						if (index === -1) index = links.length - 1;
						else index = (index - 1 + links.length) % links.length;
						focusAt(index);
						break;
					default:
						break;
				}
			},
			[],
		);

		return (
			<Box
				mb="5"
				overflowX="auto"
				sx={{
					/* Horizontal scroll when needed; avoid overflowY:hidden so line-tab * underline (negative margin) is not clipped. */
					'&::-webkit-scrollbar': {
						display: 'none',
					},
					'&': {
						msOverflowStyle: 'none',
						scrollbarWidth: 'none',
					},
				}}
			>
				<Box as="nav" aria-label={t('Section navigation')}>
					<Box
						as="ul"
						onKeyDown={onListKeyDown}
						sx={{
							display: 'flex',
							flexDirection: 'row',
							listStyleType: 'none',
							m: 0,
							p: 0,
							...styles.tablist,
						}}
					>
						{tabItems.map(({ text, fnName }, index) => {
							const isCurrent = index === tabIndex;
							const pathBuilder = goTo[fnName] as (
								...pathArgs: string[]
							) => string;
							return (
								<Box as="li" key={text}>
									<ChakraRouterLink
										data-testid={`tab-${formatAndCleanTestId(fnName)}`}
										to={handleTo(pathBuilder(...args))}
										tabIndex={
											rovingFocusIndex === index ? 0 : -1
										}
										onFocus={() =>
											setRovingFocusIndex(index)
										}
										aria-current={
											isCurrent ? 'page' : undefined
										}
										minW="max-content"
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											...tabBaseStyles,
											...(isCurrent
												? selectedTabStyles
												: {}),
										}}
									>
										{t(text)}
									</ChakraRouterLink>
								</Box>
							);
						})}
					</Box>
				</Box>
			</Box>
		);
	},
);
